


import { Component, Prop, Ref, Vue } from "vue-property-decorator";

import Calendar from "primevue/calendar";

import { I18n } from "App/IOC";

import Modal from "Web/Components/Common/Modal.vue";

/* CHAMP DE DATE AU DS DU DESKTOP — 06/10/2026 ( Fred : « norme le date picker
   comme le DS du desktop » ).

   Un champ encadre ( `.ds-input--trigger` ) qui ouvre, juste dessous, le
   calendrier habille `.ds-calendar` ( `Assets/Sass/Components/Calendar.sass` ).
   Le panneau est porte dans `#modals` et place par `v-strict-snap`, comme
   `DateTime.vue` : dans une fenetre, il passe par-dessus au lieu d'etre coupe.

   La valeur circule en `YYYY-MM-DD` ( date locale ), `""` = aucune date : c'est
   ce que les routes de l'API attendent, sans conversion d'heure en route.

   Noms de mois et de jours tires de la langue de l'interface par `Intl` ( la
   semaine commence le lundi ), jours en abrege « lun. » et non en initiale : une
   initiale ne distingue ni mardi de mercredi, ni samedi de dimanche ( regle du
   calendrier du desktop, 08/08/2026 ). */
// @ts-ignore
@Component({
    // @ts-ignore
    components: {
        Calendar,
        Modal
    }
})
export default class DatePicker extends Vue {
    @Prop( { default: "" } )
    readonly Id!: string;

    @Prop( { default: "" } )
    readonly Value!: string;

    /* `YYYY-MM-DD` ; `""` = pas de limite. */
    @Prop( { default: "" } )
    readonly MinDate!: string;

    @Prop( { default: "" } )
    readonly Placeholder!: string;

    @Prop( { default: "" } )
    readonly ClearLabel!: string;

    @Ref( "container" )
    private readonly container?: HTMLElement;

    @Ref( "panel" )
    private readonly panel?: HTMLElement;

    private opened: boolean = false;

    beforeDestroy(): void {
        window.removeEventListener( "mousedown", this.onOutsideClick );
    }

    /* La langue de l'interface ( `fr`, `en` ), celle que lit `$t`. */
    private get language(): string {
        return I18n.Locale.Id;
    }

    private get date(): Date | null {
        return this.Value !== "" ? parse( this.Value ) : null;
    }

    private get minimum(): Date | null {
        return this.MinDate !== "" ? parse( this.MinDate ) : null;
    }

    private get display(): string {
        return this.date !== null
            ? this.date.toLocaleDateString( this.language, {
                day: "numeric", month: "long", year: "numeric"
            } )
            : "";
    }

    private get locale(): any {
        const months: string[] = [];
        const monthsShort: string[] = [];
        const days: string[] = [];
        const daysShort: string[] = [];

        for( let month = 0; month < 12; month++ ) {
            const date = new Date( 2026, month, 1 );

            months.push( date.toLocaleDateString( this.language, { month: "long" } ) );
            monthsShort.push( date.toLocaleDateString( this.language, { month: "short" } ) );
        }

        /* Le 04/01/2026 est un dimanche : PrimeVue range les jours a partir du
           dimanche et les fait tourner selon `firstDayOfWeek`. */
        for( let day = 0; day < 7; day++ ) {
            const date = new Date( 2026, 0, 4 + day );

            days.push( date.toLocaleDateString( this.language, { weekday: "long" } ) );
            daysShort.push( date.toLocaleDateString( this.language, { weekday: "short" } ) );
        }

        return {
            firstDayOfWeek: 1,
            dayNames: days,
            dayNamesShort: daysShort,
            dayNamesMin: daysShort,
            monthNames: months,
            monthNamesShort: monthsShort,
            today: "",
            clear: "",
            dateFormat: "yy-mm-dd",
            weekHeader: ""
        };
    }

    private toggle(): void {
        this.opened ? this.close() : this.open();
    }

    private open(): void {
        this.opened = true;
        window.addEventListener( "mousedown", this.onOutsideClick );
    }

    private close(): void {
        this.opened = false;
        window.removeEventListener( "mousedown", this.onOutsideClick );
    }

    private onOutsideClick( event: Event ): void {
        const target = event.target as Node;

        if( this.container !== undefined && this.container.contains( target ) )
            return;

        if( this.panel !== undefined && this.panel.contains( target ) )
            return;

        this.close();
    }

    private select( date: Date ): void {
        this.$emit( "Value", format( date ) );
        this.close();
    }

    private clear(): void {
        this.$emit( "Value", "" );
        this.close();
    }
}

function pad( value: number ): string {
    return String( value ).padStart( 2, "0" );
}

function format( date: Date ): string {
    return `${date.getFullYear()}-${pad( date.getMonth() + 1 )}-${pad( date.getDate() )}`;
}

/* `new Date( "2026-10-06" )` se lit en UTC : un jour de decalage a l'ouest de
   Greenwich. On construit la date locale a la main. */
function parse( value: string ): Date | null {
    const parts = value.split( "-" ).map( Number );

    if( parts.length !== 3 || parts.some( isNaN ) )
        return null;

    return new Date( parts[ 0 ], parts[ 1 ] - 1, parts[ 2 ] );
}
